import { format } from 'd3-format'
import { findIndex, isEmpty, isNil, omitBy, isString } from 'lodash'
import i18n from 'i18next'

import { isKeyOf } from '@ahha/utils/@types/typeChecks'
import { WidgetPreset } from '@/pages/DataCamp/Dashboard/const'

export const getWidgetTitle = (widgetType: number): string => {
  if (widgetType === WidgetPreset.LINE_CHART) {
    return i18n.t('widget_type_lineChart')
  }
  if (widgetType === WidgetPreset.BAR_CHART) {
    return i18n.t('widget_type_barChart')
  }
  if (widgetType === WidgetPreset.PIE_CHART) {
    return i18n.t('widget_type_pieChart')
  }
  if (widgetType === WidgetPreset.TABLE) {
    return i18n.t('common_table')
  }
  if (widgetType === WidgetPreset.IMAGE) {
    return i18n.t('common_image')
  }
  if (widgetType === WidgetPreset.TEXT) {
    return i18n.t('common_text')
  }
  return i18n.t('widget_type_container')
}

export const checkIsOnlyDigits = (value: string) => {
  const regex = /^[0-9]*$/
  return regex.test(value)
}

export const limitDecimalPlaces = (value: string | number, decimalPlace: number) => {
  const stringValue = String(value)
  if (isEmpty(stringValue)) {
    return ''
  }
  if (stringValue.substring(stringValue.indexOf('.')).length > decimalPlace + 1) {
    return Number(stringValue.slice(0, -1))
  }
  return Number(value)
}

export const searchDictionary = (dictionary: Record<string, string>, search: string) => {
  if (isKeyOf(search, dictionary)) {
    return dictionary[search]
  }
  return null
}

export const getTotalPage = (totalCount: number | undefined, itemsPerPage: number) => Math.ceil((totalCount ?? itemsPerPage) / itemsPerPage)

export const omitNilOrEmptyStringFromObject = (obj: { [key: string]: any }) => omitBy(obj, (o) => isNil(o) || o === '')

export const getCheckpointNumber = (weightScores: Record<string, any>[] | undefined, targetWeight: Record<string, any>| undefined | null) => {
  if (!weightScores || !targetWeight) {
    return undefined
  }
  const targetIndex = findIndex(weightScores, targetWeight)
  return `Top ${targetIndex + 1}`
}

export const sanitizeFileName = (fileName: string) => (
  fileName
    .replace(/[<>:"|?*]/g, '_') // Replace forbidden characters (windows 기준)
    .replace(/\//g, '_') // Replace forward slash
    .replace(/\\/g, '_') // Replace backslash
    .replace(/\s+/g, '_') // replace spaces with underscores
    .trim()
)

export const copyToClipboard = async (text: string, e?: React.MouseEvent | React.TouchEvent): Promise<boolean> => {
  if (!text) {
    return false
  }

  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch (err) {
      console.warn('최신 API 복사 실패, 구형 방식으로 전환합니다.', err)
    }
  }

  const textArea = document.createElement('textarea')
  textArea.value = text

  textArea.style.position = 'fixed'
  textArea.style.left = '-9999px'
  textArea.style.top = '0'
  textArea.setAttribute('readonly', '')

  let mountTarget = document.body
  if (e && e.currentTarget) {
    mountTarget = e.currentTarget.parentNode as HTMLElement
  }

  mountTarget.appendChild(textArea)

  const selected = document.getSelection()!.rangeCount > 0 ? document.getSelection()!.getRangeAt(0) : false

  textArea.select()
  textArea.setSelectionRange(0, 99999) // 모바일 대응

  let success = false
  try {
    success = document.execCommand('copy')
  } catch (err) {
    console.error('Fallback Copy failed', err)
  }

  mountTarget.removeChild(textArea)

  if (selected) {
    document.getSelection()!.removeAllRanges()
    document.getSelection()!.addRange(selected)
  }

  return success
}

export const humanizeNumber = (value: number, decimalPlaces: number = 3) => {
  if (isString(value)) {
    return value
  }
  const absValue = Math.abs(value)

  // For very small numbers, use scientific notation
  if (absValue < 0.01 && value !== 0) {
    return format(`.${decimalPlaces}e`)(value)
  }

  // For numbers that don't need SI-prefix (< 1000), use fixed decimal places
  if (absValue < 1000) {
    return format(`.${decimalPlaces}~f`)(value)
  }

  // For large numbers, use SI-prefix with significant digits
  return format(`.${decimalPlaces}~s`)(value)
}
